/**
 * MediaProposalCard Component
 *
 * Interactive card for displaying media generation proposals.
 * Shows proposed variations with estimated costs and allows user approval/modification.
 *
 * Usage:
 *   <MediaProposalCard
 *     proposals={mediaGenerationProposals}
 *     onApprove={handleApprove}
 *     onSkip={handleSkip}
 *     loading={isProcessing}
 *   />
 */

import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { theme } from '@/styles/theme';

export interface MediaProposal {
  proposal_id: string;
  title: string;
  description: string;
  source_asset_id?: string;
  source_filename?: string;
  generation_mode: 'text_to_image' | 'image_to_image';
  estimated_count: number;
  estimated_credits: number;
  style: string;
  prompt_preview?: string;
}

export interface MediaProposalCardProps {
  proposals: MediaProposal[];
  onApprove: (approvedIds: string[], modifications: Record<string, string>) => void;
  onSkip: () => void;
  loading?: boolean;
  disabled?: boolean;
  /** Maximum number of proposals that can be generated at once (based on regeneration_attempts_per_campaign) */
  maxGenerations?: number;
}

export const MediaProposalCard: React.FC<MediaProposalCardProps> = ({
  proposals,
  onApprove,
  onSkip,
  loading = false,
  disabled = false,
  maxGenerations,
}) => {
  // Initialize with all proposals selected, but respect maxGenerations limit
  const initialSelection = React.useMemo(() => {
    const allIds = proposals.map(p => p.proposal_id);
    if (maxGenerations && maxGenerations > 0) {
      return allIds.slice(0, maxGenerations);
    }
    return allIds;
  }, [proposals, maxGenerations]);

  const [selectedProposals, setSelectedProposals] = useState<string[]>(initialSelection);
  const [modifications, setModifications] = useState<Record<string, string>>({});
  const [expandedProposal, setExpandedProposal] = useState<string | null>(null);

  // Sync selection when maxGenerations arrives (fixes race condition)
  useEffect(() => {
    if (maxGenerations && maxGenerations > 0 && selectedProposals.length > maxGenerations) {
      setSelectedProposals(prev => prev.slice(0, maxGenerations));
    }
  }, [maxGenerations]);

  const toggleProposal = (proposalId: string) => {
    setSelectedProposals(prev => {
      if (prev.includes(proposalId)) {
        return prev.filter(id => id !== proposalId);
      }
      // Enforce maxGenerations limit when selecting
      if (maxGenerations && maxGenerations > 0 && prev.length >= maxGenerations) {
        return prev; // Don't add more if at limit
      }
      return [...prev, proposalId];
    });
  };

  const selectAll = () => {
    const allIds = proposals.map(p => p.proposal_id);
    // Respect maxGenerations limit when selecting all
    if (maxGenerations && maxGenerations > 0) {
      setSelectedProposals(allIds.slice(0, maxGenerations));
    } else {
      setSelectedProposals(allIds);
    }
  };

  const deselectAll = () => {
    setSelectedProposals([]);
  };

  const handleModificationChange = (proposalId: string, value: string) => {
    setModifications(prev => ({
      ...prev,
      [proposalId]: value
    }));
  };

  const toggleExpand = (proposalId: string) => {
    setExpandedProposal(prev => prev === proposalId ? null : proposalId);
  };

  const handleApprove = () => {
    onApprove(selectedProposals, modifications);
  };

  const totalCredits = proposals
    .filter(p => selectedProposals.includes(p.proposal_id))
    .reduce((sum, p) => sum + p.estimated_credits, 0);

  const totalImages = proposals
    .filter(p => selectedProposals.includes(p.proposal_id))
    .reduce((sum, p) => sum + p.estimated_count, 0);

  const styleLabels: Record<string, string> = {
    'photorealistic_studio': 'Studio Photography',
    'photorealistic_lifestyle': 'Lifestyle Photography',
    'photorealistic_editorial': 'Editorial Style',
    'photorealistic_macro': 'Macro Detail',
    'stylized_3d_render': '3D Render',
    'stylized_illustration': 'Illustration',
    'stylized_cartoon': 'Cartoon Style',
    'stylized_isometric': 'Isometric View',
  };

  if (disabled) {
    return null;
  }

  return (
    <Card variant="outlined" padding="none" className="overflow-hidden">
      {/* Header */}
      <div className="p-6 bg-gradient-to-r from-purple-500/5 to-pink-500/5 border-b border-line dark:border-border-dark">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h3 className="text-xl font-bold text-ink flex items-center gap-2">
              <svg className="w-6 h-6 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              Image Generation Proposals
            </h3>
            <p className="text-sm text-ink-2 mt-1">
              I can generate variations of your product images. Review and approve the options below.
            </p>
          </div>
          <span className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-full bg-purple-100 text-purple-800 border border-purple-200">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            Awaiting Your Decision
          </span>
        </div>

        {/* Generation Limit Warning */}
        {maxGenerations && maxGenerations > 0 && proposals.length > maxGenerations && (
          <div className="mt-4 p-3 rounded-lg bg-warn-soft border border-warn/30">
            <div className="flex items-start gap-2">
              <svg className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <div>
                <p className="text-sm font-medium text-warn">
                  Generation limit: {maxGenerations} proposals maximum
                </p>
                <p className="text-xs text-warn mt-0.5">
                  You have {proposals.length} proposals but can only generate {maxGenerations} at a time. Please select the {maxGenerations} you want to generate.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Selection Controls */}
        <div className="flex items-center gap-3 mt-4">
          <span className="text-sm text-ink-2">
            {selectedProposals.length} of {maxGenerations && maxGenerations > 0 ? Math.min(proposals.length, maxGenerations) : proposals.length} selected
            {maxGenerations && maxGenerations > 0 && proposals.length > maxGenerations && (
              <span className="text-warn"> (max {maxGenerations})</span>
            )}
          </span>
          <button
            onClick={selectAll}
            className="text-sm text-purple-600 hover:underline font-medium"
          >
            {maxGenerations && maxGenerations > 0 && proposals.length > maxGenerations
              ? `Select First ${maxGenerations}`
              : 'Select All'}
          </button>
          <button
            onClick={deselectAll}
            className="text-sm text-ink-3 hover:underline"
          >
            Deselect All
          </button>
        </div>
      </div>

      {/* Proposals List */}
      <div className="p-6 space-y-4">
        {loading ? (
          <div className="py-12 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto mb-4" />
            <p className="text-ink-2">Processing your request...</p>
          </div>
        ) : (
          proposals.map((proposal) => {
            const isSelected = selectedProposals.includes(proposal.proposal_id);
            const isExpanded = expandedProposal === proposal.proposal_id;
            const isVariation = proposal.generation_mode === 'image_to_image';

            return (
              <div
                key={proposal.proposal_id}
                className={`
                  rounded-lg border-2 transition-all duration-200 overflow-hidden
                  ${isSelected
                    ? 'border-purple-500 bg-purple-50'
                    : 'border-line dark:border-border-dark bg-card dark:bg-card-darkAlt'
                  }
                `}
              >
                {/* Proposal Header */}
                <div
                  className="p-4 cursor-pointer flex items-start gap-4"
                  onClick={() => toggleProposal(proposal.proposal_id)}
                >
                  {/* Checkbox */}
                  <div
                    className={`
                      flex-shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all
                      ${isSelected
                        ? 'bg-purple-500 border-purple-500'
                        : 'border-line-2 dark:border-border-dark'
                      }
                    `}
                  >
                    {isSelected && (
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="font-semibold text-ink">
                        {proposal.title}
                      </h4>
                      {isVariation && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-work-soft text-work">
                          <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M4 3a2 2 0 100 4h12a2 2 0 100-4H4z" />
                            <path fillRule="evenodd" d="M3 8h14v7a2 2 0 01-2 2H5a2 2 0 01-2-2V8zm5 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z" clipRule="evenodd" />
                          </svg>
                          Variation
                        </span>
                      )}
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-inset text-ink-2">
                        {styleLabels[proposal.style] || proposal.style}
                      </span>
                    </div>
                    <p className="text-sm text-ink-2 mt-1">
                      {proposal.description}
                    </p>

                    {/* Source Image Info */}
                    {proposal.source_filename && (
                      <p className="text-xs text-ink-3 mt-2 flex items-center gap-1">
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                        </svg>
                        Based on: {proposal.source_filename}
                      </p>
                    )}

                    {/* Stats */}
                    <div className="flex items-center gap-4 mt-3 text-sm">
                      <span className="text-ink-2 flex items-center gap-1">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        {proposal.estimated_count} images
                      </span>
                      <span className="text-purple-600 font-medium flex items-center gap-1">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z" />
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd" />
                        </svg>
                        {proposal.estimated_credits} credits
                      </span>
                    </div>
                  </div>

                  {/* Expand Button */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleExpand(proposal.proposal_id);
                    }}
                    className="flex-shrink-0 p-2 text-ink-3 hover:text-ink-2 rounded-lg hover:bg-inset"
                  >
                    <svg
                      className={`w-5 h-5 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                </div>

                {/* Expanded Content - Modification Input */}
                {isExpanded && (
                  <div className="px-4 pb-4 border-t border-line dark:border-border-dark pt-4">
                    <label className="block text-sm font-medium text-ink-2 mb-2">
                      Add your modifications (optional)
                    </label>
                    <Input
                      placeholder="e.g., Make the background more colorful, add a sunset..."
                      value={modifications[proposal.proposal_id] || ''}
                      onChange={(e) => handleModificationChange(proposal.proposal_id, e.target.value)}
                      className="w-full"
                    />
                    <p className="text-xs text-ink-3 mt-1">
                      Your input will be added to the generation prompt.
                    </p>

                    {/* Prompt Preview */}
                    {proposal.prompt_preview && (
                      <div className="mt-3">
                        <p className="text-xs font-medium text-ink-2 mb-1">
                          Prompt Preview:
                        </p>
                        <p className="text-xs text-ink-3 bg-inset rounded p-2">
                          {proposal.prompt_preview.slice(0, 200)}...
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Actions Footer */}
      <div className="p-6 bg-ground border-t border-line dark:border-border-dark">
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
          {/* Summary */}
          <div className="text-sm text-ink-2">
            {selectedProposals.length > 0 ? (
              <span>
                Generate <strong className="text-purple-600">{totalImages}</strong> images for{' '}
                <strong className="text-purple-600">{totalCredits}</strong> credits
              </span>
            ) : (
              'Select proposals to generate, or skip to use your uploaded images'
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={onSkip}
              disabled={loading}
            >
              Skip & Use Uploads
            </Button>
            <Button
              variant="primary"
              onClick={handleApprove}
              disabled={selectedProposals.length === 0 || loading}
              loading={loading}
              icon={
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                </svg>
              }
            >
              Generate {selectedProposals.length > 0 ? `(${totalImages})` : ''}
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default MediaProposalCard;
